import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type TopCollector = {
  rank: number;
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar: string;
  // Number of posted scans in the window.
  score: number;
  // The most-recent posted scan title, used as the "item" subtitle on cards.
  item: string | null;
};

// Ranks profiles by number of posted scans within the last `windowDays` days.
// Pure aggregation — no realtime, just a one-shot fetch. The component decides
// how many to show (full board vs. top-3 strip on Home).
export function useTopCollectors(limit = 10, windowDays = 7) {
  const [collectors, setCollectors] = useState<TopCollector[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(
        Date.now() - windowDays * 24 * 60 * 60 * 1000
      ).toISOString();

      // Pull recent posted scans + group client-side. Volume is small and we
      // need the most-recent title per user anyway.
      const { data: scans, error } = await supabase
        .from("scans")
        .select("user_id,title,created_at")
        .eq("posted", true)
        .gte("created_at", since)
        .order("created_at", { ascending: false });

      if (error || !scans) {
        if (!cancelled) {
          setCollectors([]);
          setLoading(false);
        }
        return;
      }

      // Group by user_id, count, capture latest title (first because ordered desc).
      const byUser = new Map<string, { count: number; latestTitle: string }>();
      for (const s of scans) {
        const cur = byUser.get(s.user_id);
        if (cur) {
          cur.count += 1;
        } else {
          byUser.set(s.user_id, { count: 1, latestTitle: s.title });
        }
      }

      const userIds = Array.from(byUser.keys());
      if (userIds.length === 0) {
        if (!cancelled) {
          setCollectors([]);
          setLoading(false);
        }
        return;
      }

      const { data: profiles } = await (supabase as any)
        .from("profiles_public")
        .select("id,handle,display_name,avatar")
        .in("id", userIds);

      const profById = new Map(
        ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );

      const ranked: TopCollector[] = userIds
        .map((id) => {
          const v = byUser.get(id)!;
          const p = profById.get(id);
          return {
            user_id: id,
            handle: p?.handle ?? "anon",
            display_name: p?.display_name ?? null,
            avatar: p?.avatar ?? "olive",
            score: v.count,
            item: v.latestTitle,
            rank: 0, // assigned below
          };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((c, i) => ({ ...c, rank: i + 1 }));

      if (!cancelled) {
        setCollectors(ranked);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [limit, windowDays]);

  return { collectors, loading };
}
